import type {
  CompleteOnboardingInput,
  CompleteOnboardingResult,
  ExtractLinkedInInput,
  GenerateQuizInput,
  GenerateQuizResult,
  GetOnboardingDraftResult,
  LinkedInExtractionResult,
  OnboardingApi,
  OnboardingOptions,
  OnboardingStepStatus,
  OnboardingValidationIssue,
  ParseCvFileInput,
  ParseCvInput,
  ParseCvResult,
  PersonalizedQuestionInput,
  PersonalizedQuestionOption,
  PersonalizedQuestionResult,
  SaveOnboardingStepInput,
  SaveOnboardingStepResult,
  SaveChatMemoryInput,
  SaveChatMemoryResult,
  SuggestFocusAreasInput,
  SuggestFocusAreasResult,
  SuggestRolesIndustriesInput,
  SuggestRolesIndustriesResult,
  SuggestValuesInput,
  SuggestValuesResult,
} from "@/features/onboarding/contracts";
import {
  onboardingStepOrder,
  type CareerGoalKind,
  type GoalTimeframe,
  type NetworkSourceKind,
  type OnboardingAnswers,
  type OnboardingDraft,
  type OnboardingGoal,
  type OnboardingManualContact,
  type OnboardingRelationshipComfort,
  type OnboardingSearchIntent,
  type OnboardingStepId,
  type OnboardingTarget,
  type SearchTrack,
  type WorkModePreference,
} from "@/features/onboarding/types";
import { httpClient, ApiError } from "@/shared/api/httpClient";
import { getClientFlowId } from "@/shared/api/sessionStorage";
import { reportError } from "@/shared/sentry";

// ─── Backend wire types ──────────────────────────────────────────────

interface BackendProfile {
  fullName?: string | null;
  university?: string | null;
  address?: string | null;
  major?: string | null;
  minor?: string | null;
  graduationYear?: number | string | null;
}

interface BackendUserGoals {
  target_role?: string | null;
  target_roles?: string[];
  target_industries?: string[];
  target_companies?: string[];
  location?: string | null;
  locations?: string[];
  work_setup?: "remote" | "hybrid" | "office" | "any" | null;
  team_size?: "small" | "medium" | "large" | "any" | null;
  day_to_day?: string | null;
  growth_mentorship?: string | null;
  looking_for?: "internship" | "fulltime" | "exploring" | "career-change" | null;
  values?: string[];
  focus_areas?: string[];
  timeline?: "asap" | "3mo" | "6mo" | "12mo" | "exploring" | null;
  major?: string | null;
  graduation_date?: number | null;
  goal_clarity?: "clear" | "exploring" | null;
  goal_brief?: string | null;
  target_outcome?: string | null;
  goal_kind?: string | null;
  searchIntent?: OnboardingSearchIntent | null;
  salary_expectation?: {
    min?: number;
    max?: number;
    currency?: "USD";
    period?: "year" | "hour";
  } | null;
  work_authorization?: {
    status?: "citizen" | "permanent_resident" | "authorized" | "needs_sponsorship" | "other";
    needsSponsorship?: boolean;
    details?: string | null;
  } | null;
}

interface BackendParsedCvExperience {
  jobTitle?: string | null;
  companyName?: string | null;
  employmentDates?: string | null;
  location?: string | null;
  industries?: string[];
}

interface BackendParsedCvResult {
  fullName?: string | null;
  location?: string | null;
  education?: {
    institution?: string | null;
    degree?: string | null;
    major?: string | null;
    gradYear?: number | null;
  } | null;
  hardSkills?: string[];
  softSkills?: string[];
  certifications?: string[];
  experience?: BackendParsedCvExperience[];
  experienceSummary?: string | null;
  /** Next-move titles from the CV parser - not past job titles. */
  suggestedTargetRoles?: string[];
}

interface UploadCvResponse {
  jobId: string;
  status: "pending";
}

interface CvParseJobResponse {
  jobId: string;
  status: "pending" | "completed" | "failed";
  result: BackendParsedCvResult | null;
  errorMessage?: string | null;
}

interface BackendMe {
  user: { id: string; email: string; fullName: string | null };
  sessionId: string;
  hasCompletedOnboarding: boolean;
}

interface CreateContactDto {
  fullName: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  company?: string;
  role?: string;
  jobTitle?: string;
  location?: string;
  linkedinUrl?: string;
  phoneNumber?: string;
  professionalNote?: string;
}

// ─── Options surfaced to the UI (mirrors mockOptions) ────────────────

const defaultRelationshipComfort: OnboardingRelationshipComfort = {
  outreachComfort: "known_contacts_first",
  introPreference: "ask_for_context",
  allowDraftGeneration: true,
  boundaries: [
    { id: "no-cold-dms", label: "Do not suggest cold DMs without context", enabled: true },
    { id: "draft-first", label: "Show me drafts before anything is sent", enabled: true },
    { id: "protect-close-ties", label: "Be careful with close personal relationships", enabled: true },
  ],
  notes: "",
};

const realOptions: OnboardingOptions = {
  acceptedResumeTypes: [".pdf", ".txt"],
  suggestedRoles: [
    "Product Manager",
    "Product Operations",
    "Software Engineer",
    "Design Program Manager",
    "Chief of Staff",
  ],
  suggestedCompanies: ["Watershed", "Stripe", "Notion", "Figma", "Anthropic"],
  suggestedIndustries: ["Climate tech", "AI tools", "Healthcare", "Education", "Fintech"],
  suggestedDomains: ["B2B SaaS", "Developer tools", "Sustainability", "Student programs"],
  networkSourceKinds: ["google", "linkedin", "csv_import", "manual_contacts"] satisfies NetworkSourceKind[],
  defaultRelationshipComfort,
  relationshipBoundaryTemplates: defaultRelationshipComfort.boundaries.map(({ id, label }) => ({ id, label })),
};

// ─── localStorage answers cache ──────────────────────────────────────
// Fields like OnboardingGoal.headline / targetOutcome / kind have no backend
// home today. We cache the full OnboardingAnswers per session so refresh
// mid-flow doesn't lose them. The cache is cleared on complete().

function cacheKey(): string | null {
  return `liminl.onboarding.${getClientFlowId()}`;
}

function readCachedAnswers(): Partial<OnboardingAnswers> | null {
  const key = cacheKey();
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Partial<OnboardingAnswers>) : null;
  } catch {
    return null;
  }
}

function writeCachedAnswers(answers: OnboardingAnswers): void {
  const key = cacheKey();
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(answers));
  } catch {
    // best effort
  }
}

function clearCachedAnswers(): void {
  const key = cacheKey();
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // best effort
  }
}

// ─── Type mappers ────────────────────────────────────────────────────

function workModeToWorkSetup(wm: WorkModePreference): BackendUserGoals["work_setup"] {
  switch (wm) {
    case "remote": return "remote";
    case "hybrid": return "hybrid";
    case "onsite": return "office";
    case "open": return "any";
  }
}
function workSetupToWorkMode(ws: BackendUserGoals["work_setup"]): WorkModePreference {
  switch (ws) {
    case "remote": return "remote";
    case "hybrid": return "hybrid";
    case "office": return "onsite";
    default: return "open";
  }
}

function searchTrackToLookingFor(st: SearchTrack): BackendUserGoals["looking_for"] {
  switch (st) {
    case "internship": return "internship";
    case "full_time": return "fulltime";
    case "grad_school_prep": return "exploring";
    case "exploration": return "exploring";
  }
}
function lookingForToSearchTrack(lf: BackendUserGoals["looking_for"]): SearchTrack {
  switch (lf) {
    case "internship": return "internship";
    case "fulltime": return "full_time";
    case "career-change": return "exploration";
    default: return "exploration";
  }
}

function timeframeToTimeline(t: GoalTimeframe): BackendUserGoals["timeline"] {
  switch (t) {
    case "now": return "asap";
    case "one_to_three_months": return "3mo";
    case "three_to_six_months": return "6mo";
    case "six_months_plus": return "12mo";
    case "exploratory": return "exploring";
  }
}
function timelineToTimeframe(t: BackendUserGoals["timeline"]): GoalTimeframe {
  switch (t) {
    case "asap": return "now";
    case "3mo": return "one_to_three_months";
    case "6mo": return "three_to_six_months";
    case "12mo": return "six_months_plus";
    default: return "exploratory";
  }
}

function goalKindFromBackend(clarity: BackendUserGoals["goal_clarity"]): CareerGoalKind {
  // main collapsed "explore_options" into "liminl_decide" - exploring goal
  // clarity maps to the let-Liminl-decide kind on the new flow.
  return clarity === "exploring" ? "liminl_decide" : "find_next_role";
}

/** High-confidence hard-nos from dealbreaker text → matcher exclusion hints. */
function inferenceExclusionsFromFocusAreas(focusAreas: string[]) {
  const roleFamilies: string[] = [];
  const industries: string[] = [];
  const companyArchetypes: string[] = [];
  for (const area of focusAreas) {
    const match = area.match(/\b(?:no|not|avoid|never|don't want|do not want)\s+(.+)/i);
    if (!match?.[1]) continue;
    const phrase = match[1].replace(/[.!?]+$/, "").trim();
    if (!phrase || phrase.length > 80) continue;
    if (/\b(travel|on[- ]?call|weekend|relocation)\b/i.test(phrase)) {
      companyArchetypes.push(phrase);
    } else if (/\b(finance|banking|healthcare|crypto|adtech|consulting|agency)\b/i.test(phrase)) {
      industries.push(phrase);
    } else {
      roleFamilies.push(phrase);
    }
  }
  if (roleFamilies.length === 0 && industries.length === 0 && companyArchetypes.length === 0) {
    return undefined;
  }
  return {
    roleFamilies,
    adjacentTitles: [] as string[],
    industries,
    companyArchetypes,
    adjacentCompanies: [] as string[],
  };
}

function onboardingGoalToUserGoals(goal: OnboardingGoal): BackendUserGoals {
  // All role targets (plural) - backend's target_roles[] replaces the legacy
  // singular target_role. The first row also becomes the primary; we send
  // both for back-compat.
  const roleTargets = goal.targets
    .filter((t) => t.kind === "role")
    .map((t) => t.roleTitle ?? t.label);
  const companyTargets = goal.targets
    .filter((t) => t.kind === "company")
    .map((t) => t.companyName ?? t.label);
  const industryTargets = goal.targets
    .filter((t) => t.kind === "industry")
    .map((t) => t.industryName ?? t.label);
  const domainTargets = goal.targets
    .filter((t) => t.kind === "domain")
    .map((t) => t.domainName ?? t.label);

  const focusAreas = Array.from(
    new Set([...(goal.focusAreas ?? []).map((area) => area.trim()).filter(Boolean), ...domainTargets]),
  );

  return {
    // Omit empty role / focus lists so a later prefs PUT (companies-only, etc.)
    // cannot wipe roles or must-haves already saved. Backend preserve-on-omit
    // skips replace when these keys are absent; explicit [] would clear.
    ...(roleTargets.length > 0
      ? { target_role: roleTargets[0], target_roles: roleTargets }
      : {}),
    target_industries: industryTargets,
    target_companies: companyTargets,
    location: goal.locations[0] ?? null,
    locations: goal.locations,
    work_setup: workModeToWorkSetup(goal.workMode),
    // ASAP / 1-3 months → Active (fulltime). Exploratory → exploring.
    // Otherwise fall back to searchTrack mapping.
    looking_for:
      goal.timeframe === "now" || goal.timeframe === "one_to_three_months"
        ? "fulltime"
        : goal.timeframe === "exploratory"
          ? "exploring"
          : searchTrackToLookingFor(goal.searchTrack),
    // values + foundation come from Phase 2b (foundation step). Empty in V1.
    values: [],
    ...(focusAreas.length > 0 ? { focus_areas: focusAreas } : {}),
    timeline: timeframeToTimeline(goal.timeframe),
    goal_clarity: goal.kind === "liminl_decide" ? "exploring" : "clear",
    // New-repo step 3 fields that have no backend column - backend now
    // persists them into OnboardingProfile.lovableGoalsExtra sidecar JSON.
    goal_brief: goal.headline.trim() || null,
    target_outcome: goal.targetOutcome.trim() || null,
    goal_kind: goal.kind,
    // Structured preferences (career_preferences step). Spread conditionally so
    // an unset value never overwrites a previously-saved one on the wire merge.
    ...(goal.salaryMin != null || goal.salaryMax != null
      ? {
          salary_expectation: {
            min: goal.salaryMin,
            max: goal.salaryMax,
            currency: goal.salaryCurrency ?? "USD",
            period: goal.salaryPeriod ?? "year",
          },
        }
      : {}),
    ...(goal.workAuthorization
      ? {
          work_authorization: {
            status: goal.workAuthorization.status,
            needsSponsorship: goal.workAuthorization.needsSponsorship,
            details: goal.workAuthorization.details ?? null,
          },
        }
      : {}),
    // Keep these omitted so the goals adapter doesn't clobber them.
    // day_to_day / growth_mentorship / team_size / major / graduation_date
    // are managed in lovableGoalsExtra by other flows.
  };
}

function userGoalsToOnboardingGoal(g: BackendUserGoals): OnboardingGoal | null {
  // Plural target_roles[] is preferred; fall back to singular for back-compat.
  const roleLabels = (g.target_roles && g.target_roles.length > 0)
    ? g.target_roles
    : g.target_role
      ? [g.target_role]
      : [];
  const hasAnyTarget =
    roleLabels.length > 0 ||
    (g.target_industries?.length ?? 0) > 0 ||
    (g.target_companies?.length ?? 0) > 0 ||
    (g.focus_areas?.length ?? 0) > 0;
  if (!hasAnyTarget && !g.timeline && !g.work_setup && !g.location && !g.looking_for && !g.goal_brief && !g.target_outcome) {
    return null;
  }

  const targets: OnboardingTarget[] = [];
  roleLabels.forEach((label, i) => {
    targets.push({
      id: `role-${i}`,
      kind: "role",
      label,
      roleTitle: label,
      priority: i === 0 ? "primary" : "secondary",
      tags: [],
    });
  });
  (g.target_companies ?? []).forEach((name, i) => {
    targets.push({
      id: `company-${i}`,
      kind: "company",
      label: name,
      companyName: name,
      priority: targets.length === 0 ? "primary" : "secondary",
      tags: [],
    });
  });
  (g.target_industries ?? []).forEach((name, i) => {
    targets.push({
      id: `industry-${i}`,
      kind: "industry",
      label: name,
      industryName: name,
      priority: "secondary",
      tags: [],
    });
  });
  (g.focus_areas ?? []).forEach((name, i) => {
    targets.push({
      id: `domain-${i}`,
      kind: "domain",
      label: name,
      domainName: name,
      priority: "watch",
      tags: [],
    });
  });

  // Prefer the persisted goal_kind (round-trippable) over the lossy
  // goal_clarity inference. Fall back to clarity for accounts that pre-date
  // the goal_kind sidecar key.
  const kind: OnboardingGoal["kind"] =
    g.goal_kind && isCareerGoalKind(g.goal_kind)
      ? g.goal_kind
      : goalKindFromBackend(g.goal_clarity);

  return {
    kind,
    headline: g.goal_brief?.trim() ?? "",
    targetOutcome: g.target_outcome?.trim() ?? "",
    timeframe: timelineToTimeframe(g.timeline),
    searchTrack: lookingForToSearchTrack(g.looking_for),
    locations:
      g.locations && g.locations.length > 0
        ? g.locations
        : g.location
          ? [g.location]
          : [],
    workMode: workSetupToWorkMode(g.work_setup),
    targets,
    notes: g.day_to_day ?? "",
    salaryMin: g.salary_expectation?.min,
    salaryMax: g.salary_expectation?.max,
    salaryCurrency: g.salary_expectation?.currency,
    salaryPeriod: g.salary_expectation?.period,
    workAuthorization: g.work_authorization
      ? {
          status: g.work_authorization.status,
          needsSponsorship: g.work_authorization.needsSponsorship,
          details: g.work_authorization.details ?? null,
        }
      : undefined,
  };
}

const CAREER_GOAL_KINDS = new Set<OnboardingGoal["kind"]>([
  "find_next_role",
  "change_function",
  "build_network",
  "liminl_decide",
  "other",
]);

function isCareerGoalKind(value: string): value is OnboardingGoal["kind"] {
  return CAREER_GOAL_KINDS.has(value as OnboardingGoal["kind"]);
}

function manualContactToDto(c: OnboardingManualContact): CreateContactDto | null {
  if (!c.fullName || c.fullName.trim().length === 0) return null;
  const [first, ...rest] = c.fullName.trim().split(/\s+/);
  return {
    fullName: c.fullName.trim(),
    firstName: first ?? c.fullName,
    lastName: rest.length > 0 ? rest.join(" ") : undefined,
    email: c.email?.trim() || undefined,
    company: c.company?.trim() || undefined,
    role: c.role?.trim() || undefined,
    jobTitle: c.role?.trim() || undefined,
    linkedinUrl: c.linkedinUrl?.trim() || undefined,
    professionalNote: c.notes?.trim() || c.relationshipContext?.trim() || undefined,
  };
}

// ─── Step validation (mirror of mock; kept local so we don't drag the mock in) ──

const hasText = (value: string | undefined) => Boolean(value?.trim());
const issue = (
  field: string,
  message: string,
  severity: OnboardingValidationIssue["severity"] = "error",
): OnboardingValidationIssue => ({ field, message, severity });

function validateStep(answers: OnboardingAnswers, stepId: OnboardingStepId): OnboardingValidationIssue[] {
  switch (stepId) {
    case "resume": {
      const resume = answers.resume;
      if (!resume) return [issue("resume", "Add a resume or CV to continue.")];
      return [
        !hasText(resume.fileName) ? issue("fileName", "Add a resume or CV file.") : null,
      ].filter(Boolean) as OnboardingValidationIssue[];
    }
    case "network":
      if (answers.networkSources.length === 0) {
        return [issue("networkSources", "Choose at least one way to add your network.", "info")];
      }
      return [];
    case "goal": {
      const goal = answers.goal;
      if (!goal) return [issue("goal", "Tell Liminl what to optimize for.")];
      return [
        !hasText(goal.headline) ? issue("headline", "Add a short goal headline.") : null,
        !hasText(goal.targetOutcome)
          ? issue("targetOutcome", "Describe the outcome Liminl should optimize for.")
          : null,
        goal.kind !== "liminl_decide" && goal.targets.length === 0
          ? issue("targets", "Add at least one role, company, industry, or domain.")
          : null,
      ].filter(Boolean) as OnboardingValidationIssue[];
    }
    case "review":
      if (!answers.review) {
        return [issue("review", "Review the MAP brief before completing onboarding.", "info")];
      }
      return [];
  }
}

function isStepEmpty(answers: OnboardingAnswers, stepId: OnboardingStepId): boolean {
  switch (stepId) {
    case "resume": return answers.resume === null;
    case "network": return answers.networkSources.length === 0 && answers.manualContacts.length === 0;
    case "goal": return answers.goal === null;
    case "review": return answers.review === null;
  }
}

function getStepStatuses(draft: OnboardingDraft): OnboardingStepStatus[] {
  return onboardingStepOrder.map((stepId) => {
    const issues = validateStep(draft.answers, stepId);
    const hasErrors = issues.some((i) => i.severity === "error");
    const empty = isStepEmpty(draft.answers, stepId);
    return {
      stepId,
      state: empty
        ? "empty"
        : hasErrors
          ? "invalid"
          : draft.completedSteps.includes(stepId)
            ? "saved"
            : "valid",
      issues,
    };
  });
}

// ─── Backend reads ───────────────────────────────────────────────────

async function fetchProfile(): Promise<BackendProfile | null> {
  try {
    return await httpClient.get<BackendProfile | null>("/api/onboarding/profile");
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

async function fetchGoals(): Promise<BackendUserGoals> {
  try {
    return await httpClient.get<BackendUserGoals>("/api/onboarding/goals");
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return {};
    throw err;
  }
}

async function fetchMe(): Promise<BackendMe | null> {
  try {
    return await httpClient.get<BackendMe>("/api/auth/me");
  } catch {
    return null;
  }
}

// ─── Project backend state into OnboardingDraft.answers ──────────────

function projectAnswers(
  profile: BackendProfile | null,
  goals: BackendUserGoals,
  cache: Partial<OnboardingAnswers> | null,
): OnboardingAnswers {
  const cachedGoal = cache?.goal ?? null;
  const backendGoal = userGoalsToOnboardingGoal(goals);

  // Prefer cached headline/targetOutcome/kind/notes over backend (which doesn't
  // store them). Targets/locations/workMode/timeframe/searchTrack come from
  // backend (source of truth), falling back to cache when backend is empty.
  let goal: OnboardingGoal | null = null;
  if (backendGoal && cachedGoal) {
    goal = {
      ...backendGoal,
      headline: cachedGoal.headline || backendGoal.headline,
      targetOutcome: cachedGoal.targetOutcome || backendGoal.targetOutcome,
      kind: cachedGoal.kind || backendGoal.kind,
      notes: cachedGoal.notes || backendGoal.notes,
    };
  } else if (backendGoal) {
    goal = backendGoal;
  } else if (cachedGoal) {
    goal = cachedGoal;
  }

  // Profile fields (when collected by a future Identity step) aren't read back
  // here - V1 doesn't have an Identity step yet, so profile reads are for
  // display-name fallback only.
  void profile;

  return {
    resume: cache?.resume ?? null,
    networkSources: cache?.networkSources ?? [],
    manualContacts: cache?.manualContacts ?? [],
    goal,
    relationshipComfort: cache?.relationshipComfort ?? defaultRelationshipComfort,
    searchIntent: goals.searchIntent ?? cache?.searchIntent ?? null,
    review: cache?.review ?? null,
    careerQuiz: cache?.careerQuiz ?? null,
    phone: cache?.phone ?? null,
  };
}

function deriveCompletedSteps(answers: OnboardingAnswers): OnboardingStepId[] {
  const completed: OnboardingStepId[] = [];
  if (answers.resume) completed.push("resume");
  if (answers.networkSources.length > 0 || answers.manualContacts.length > 0) completed.push("network");
  if (answers.goal) completed.push("goal");
  // "review" is only marked on complete() success
  return completed;
}

function makeDraft(answers: OnboardingAnswers, sessionId: string, revision: number): OnboardingDraft {
  const completedSteps = deriveCompletedSteps(answers);
  const nextStep =
    onboardingStepOrder.find((s) => !completedSteps.includes(s)) ?? "review";
  return {
    id: `live-${sessionId}`,
    userId: sessionId, // placeholder; the UI never reads this beyond the cache lookup
    status: completedSteps.length === 0 ? "not_started" : "in_progress",
    currentStep: nextStep,
    completedSteps,
    answers,
    revision,
    createdAt: new Date(0).toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  return Array.from(
    new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value))),
  );
}

function nativeCvToParseResult(parsed: BackendParsedCvResult): ParseCvResult {
  const experiences = parsed.experience ?? [];
  const industries = uniqueNonEmpty(experiences.flatMap((experience) => experience.industries ?? []));
  const historyTitles = new Set(
    uniqueNonEmpty(experiences.map((experience) => experience.jobTitle)).map((title) =>
      title.trim().toLowerCase(),
    ),
  );
  // Prefer Claude next-move suggestions. Never fall back to resume history
  // titles - those get filtered client-side and left the chip row empty.
  const suggestedTargetRoles = uniqueNonEmpty(parsed.suggestedTargetRoles ?? []).filter(
    (role) => !historyTitles.has(role.trim().toLowerCase()),
  );

  return {
    extracted: {
      email: "",
      experiences: experiences.map((experience) => ({
        company: experience.companyName ?? "",
        dates: experience.employmentDates ?? "",
        location: experience.location ?? "",
        role: experience.jobTitle ?? "",
      })),
      focusAreas: [],
      fullName: parsed.fullName ?? "",
      graduationYear:
        parsed.education?.gradYear !== null && parsed.education?.gradYear !== undefined
          ? String(parsed.education.gradYear)
          : "",
      location: parsed.location ?? "",
      major: parsed.education?.major ?? "",
      minor: "",
      skills: uniqueNonEmpty([
        ...(parsed.hardSkills ?? []),
        ...(parsed.softSkills ?? []),
        ...(parsed.certifications ?? []),
      ]),
      suggestedTargetIndustries: industries,
      suggestedTargetRoles,
      summary: parsed.experienceSummary ?? "",
      university: parsed.education?.institution ?? "",
      values: [],
    },
  };
}

async function pollCvParseJob(jobId: string): Promise<ParseCvResult> {
  const deadline = Date.now() + 45_000;

  while (Date.now() < deadline) {
    await wait(1_500);
    const job = await httpClient.get<CvParseJobResponse>(
      `/api/onboarding-v2/cv/jobs/${encodeURIComponent(jobId)}`,
    );

    if (job.status === "completed" && job.result) {
      return nativeCvToParseResult(job.result);
    }

    if (job.status === "failed") {
      throw new Error(job.errorMessage ?? "Liminl could not read this resume.");
    }
  }

  throw new Error("Liminl is still reviewing this resume. Try again in a moment.");
}

async function parsePdfCv(file: File): Promise<ParseCvResult> {
  const formData = new FormData();
  formData.append("file", file);
  const upload = await httpClient.postForm<UploadCvResponse>("/api/onboarding-v2/cv/upload", formData);
  return pollCvParseJob(upload.jobId);
}

async function parseTextCv(file: File): Promise<ParseCvResult> {
  const text = (await file.text()).replace(/\s+/g, " ").trim();
  if (text.length < 30) {
    throw new Error("Liminl could not read enough text from this file. Upload a PDF resume instead.");
  }
  return httpClient.post<ParseCvResult>("/api/onboarding-v2/parse-cv", { text });
}

function formatExperienceAt(
  experience: { role?: string; company?: string } | null | undefined,
): string {
  const role = experience?.role?.trim() ?? "";
  const company = experience?.company?.trim() ?? "";
  if (role && company) return `${role} at ${company}`;
  return role || company;
}

/** Shared profile Intro / reflection payload for complete + mid-flow chat memory. */
function buildProfileReflectionFields(answers: OnboardingAnswers): {
  confirmedFullName: string;
  experienceSummary: string;
  linkedinUrl: string;
  writtenReflection: string;
} {
  const quickChat = answers.resume?.quickChat;
  const parsed = answers.resume?.parsedCv;
  const linkedinUrl =
    answers.resume?.parsedSignals.find((signal) => signal.id === "linkedin-url")?.value?.trim() ||
    "";

  const current =
    quickChat?.currentRoleCompany?.trim() ||
    formatExperienceAt(parsed?.experiences?.[0]) ||
    "";
  const previousFromChat = quickChat?.previousWork?.trim() || "";
  const previousFromParsed = (parsed?.experiences ?? [])
    .slice(1, 4)
    .map((experience) => formatExperienceAt(experience))
    .filter(Boolean)
    .join("; ");
  const previous = previousFromChat || previousFromParsed;
  const signal = quickChat?.tenure?.trim() || parsed?.summary?.trim() || "";

  const experienceSummary =
    current ||
    parsed?.summary?.trim() ||
    answers.goal?.notes?.match(/Current path: ([^\n]+)/)?.[1]?.trim() ||
    "";

  const writtenReflection = [
    current ? `Current: ${current}` : null,
    previous ? `Previously: ${previous}` : null,
    signal ? `Signal: ${signal}` : null,
    answers.goal?.headline ? `Goal: ${answers.goal.headline}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  return {
    confirmedFullName: parsed?.fullName?.trim() || "",
    experienceSummary,
    linkedinUrl,
    writtenReflection,
  };
}

// ─── The adapter ─────────────────────────────────────────────────────
// Revision is a client-local counter; the backend has no OCC for onboarding.

let currentRevision = 1;

export const realOnboardingApi: OnboardingApi = {
  async getDraft(): Promise<GetOnboardingDraftResult> {
    const sid = getClientFlowId();
    const [profile, goals] = await Promise.all([fetchProfile(), fetchGoals()]);
    const cache = readCachedAnswers();
    const answers = projectAnswers(profile, goals, cache);
    const draft = makeDraft(answers, sid, currentRevision);

    return {
      draft,
      options: realOptions,
      stepStatuses: getStepStatuses(draft),
    };
  },

  async saveStep(input: SaveOnboardingStepInput): Promise<SaveOnboardingStepResult> {
    const sid = getClientFlowId();
    const cache = readCachedAnswers() ?? {};

    // Apply the payload to a working answers snapshot.
    const baseAnswers: OnboardingAnswers = {
      resume: cache.resume ?? null,
      networkSources: cache.networkSources ?? [],
      manualContacts: cache.manualContacts ?? [],
      goal: cache.goal ?? null,
      relationshipComfort: cache.relationshipComfort ?? defaultRelationshipComfort,
      searchIntent: cache.searchIntent ?? null,
      review: cache.review ?? null,
      careerQuiz: cache.careerQuiz ?? null,
      phone: cache.phone ?? null,
    };
    baseAnswers.searchIntent = input.payload.searchIntent ?? baseAnswers.searchIntent;

    switch (input.stepId) {
      case "resume":
        baseAnswers.resume = input.payload.resume;
        break;
      case "network":
        baseAnswers.networkSources = input.payload.networkSources;
        baseAnswers.manualContacts = input.payload.manualContacts;
        break;
      case "goal":
        baseAnswers.goal = input.payload.goal;
        baseAnswers.careerQuiz = input.payload.careerQuiz ?? baseAnswers.careerQuiz;
        break;
      case "review":
        baseAnswers.review = input.payload.review;
        if ("phone" in input.payload) baseAnswers.phone = input.payload.phone ?? null;
        break;
    }

    // Goal is the only step with a per-step backend write. The rest defer
    // until complete() so we don't fire bulk POST /contacts on every refresh.
    if (input.stepId === "goal" && baseAnswers.goal) {
      const lastGoals = await fetchGoals();
      const merged: BackendUserGoals = {
        ...lastGoals,
        ...onboardingGoalToUserGoals(baseAnswers.goal),
      };
      await httpClient.put("/api/onboarding/goals", merged as unknown as Record<string, unknown>);
    }

    writeCachedAnswers(baseAnswers);
    currentRevision += 1;
    const draft = makeDraft(baseAnswers, sid, currentRevision);

    return {
      draft,
      stepStatuses: getStepStatuses(draft),
      savedAt: draft.updatedAt,
    };
  },

  async complete(_input: CompleteOnboardingInput): Promise<CompleteOnboardingResult> {
    const cache = readCachedAnswers() ?? {};
    const sid = getClientFlowId();

    const answers: OnboardingAnswers = {
      resume: cache.resume ?? null,
      networkSources: cache.networkSources ?? [],
      manualContacts: cache.manualContacts ?? [],
      goal: cache.goal ?? null,
      relationshipComfort: cache.relationshipComfort ?? defaultRelationshipComfort,
      searchIntent: cache.searchIntent ?? null,
      review: cache.review ?? null,
      careerQuiz: cache.careerQuiz ?? null,
      phone: cache.phone ?? null,
    };

    const me = await fetchMe();
    const displayName =
      me?.user.fullName?.trim() || me?.user.email.split("@")[0] || "Liminl user";

    // Optional save steps are best-effort: a failed save must NOT prevent the
    // user from completing onboarding. Each failure is logged and reported but
    // swallowed so we still reach the strict completion call below.
    const bestEffort = async (label: string, fn: () => Promise<unknown>): Promise<void> => {
      try {
        await fn();
      } catch (caught) {
        console.error(`[onboarding.complete] optional step failed: ${label}`, caught);
        reportError(caught);
      }
    };

    // 1. Ensure profile row exists (POST /onboarding/complete requires it).
    //    Chat / resume / LinkedIn map to experience_summary + written_reflection.
    //    LinkedIn URL is persisted when present on the resume signals.
    const profileFields = buildProfileReflectionFields(answers);
    const experienceSummary = profileFields.experienceSummary;
    const writtenReflection = profileFields.writtenReflection;
    const confirmedFullName = profileFields.confirmedFullName;
    const linkedinUrl = profileFields.linkedinUrl;

    await httpClient.patch("/api/onboarding/profile", {
      display_name: displayName,
      ...(confirmedFullName ? { full_name: confirmedFullName } : {}),
      ...(writtenReflection ? { written_reflection: writtenReflection } : {}),
      ...(experienceSummary ? { experience_summary: experienceSummary } : {}),
      ...(linkedinUrl ? { linkedin_url: linkedinUrl } : {}),
    });

    // 2. PUT goals (strict when goal present).
    const goal = answers.goal;
    if (goal) {
      const lastGoals = await fetchGoals();
      const merged: BackendUserGoals = {
        ...lastGoals,
        ...onboardingGoalToUserGoals(goal),
      };
      await httpClient.put("/api/onboarding/goals", merged as unknown as Record<string, unknown>);

      // 3. SearchCriteria for job matching. Strict when goal exists (open role OK).
      const roleLabel =
        (
          goal.targets.find((t) => t.kind === "role")?.roleTitle ??
          goal.targets.find((t) => t.kind === "role")?.label ??
          goal.headline?.trim() ??
          ""
        ) || "Open role search";
      const arrangements =
        goal.workMode === "open"
          ? (["onsite", "hybrid", "remote"] as const)
          : goal.workMode === "remote"
            ? (["remote"] as const)
            : goal.workMode === "hybrid"
              ? (["hybrid"] as const)
              : goal.workMode === "onsite"
                ? (["onsite"] as const)
                : (["onsite", "hybrid", "remote"] as const);
      const sectors = goal.targets
        .filter((t) => t.kind === "industry")
        .map((t) => t.industryName ?? t.label)
        .filter(Boolean);
      const pinnedTargets = goal.targets
        .filter((t) => t.kind === "company")
        .map((t) => ({ companyName: (t.companyName ?? t.label).trim() }))
        .filter((t) => t.companyName.length > 0)
        .slice(0, 50);
      const parsed = answers.resume?.parsedCv;
      const titles = Array.from(
        new Set(
          [
            ...(parsed?.experiences?.map((experience) => experience.role) ?? []),
            ...(parsed?.suggestedTargetRoles ?? []),
            roleLabel !== "Open role search" ? roleLabel : null,
          ].filter((value): value is string => Boolean(value?.trim())),
        ),
      );
      const skills = parsed?.skills ?? [];
      const current =
        titles.length > 0 || skills.length > 0 || experienceSummary
          ? {
              ...(titles.length > 0 ? { titles } : {}),
              ...(skills.length > 0 ? { skills } : {}),
              ...(experienceSummary ? { summary: experienceSummary } : {}),
            }
          : undefined;
      const inferenceExclusions = inferenceExclusionsFromFocusAreas(goal.focusAreas ?? []);
      await httpClient.post("/api/onboarding/search-criteria", {
        role: roleLabel.trim() || "Open role search",
        sectors: sectors.length > 0 ? sectors : ["general"],
        geo: goal.locations[0]?.trim() || "Open",
        seniorityBand: goal.seniorityBand ?? "MID",
        ...(pinnedTargets.length > 0 ? { pinnedTargets } : {}),
        ...(current ? { current } : {}),
        jobTrackerPreferences: {
          roleAliases: [],
          salaryMin: goal.salaryMin ?? null,
          salaryMax: goal.salaryMax ?? null,
          workArrangements: [...arrangements],
          employmentTypes: ["full_time"],
          ...(inferenceExclusions ? { inferenceExclusions } : {}),
        },
      });
      await bestEffort("rebuildSearchProfile", () =>
        httpClient.post("/api/jobs/search-profile/rebuild", {}),
      );
    }

    // 4. Bulk POST contacts (best-effort).
    const dtos = answers.manualContacts
      .map(manualContactToDto)
      .filter((dto): dto is CreateContactDto => dto !== null);
    if (dtos.length > 0) {
      await bestEffort("bulk contacts", () =>
        httpClient.post("/api/network/contacts/bulk", { contacts: dtos }),
      );
    }

    // 5. Mark onboarding completed (strict).
    await httpClient.post("/api/onboarding/complete", {});

    clearCachedAnswers();
    currentRevision += 1;
    const completedDraft: OnboardingDraft = {
      ...makeDraft(answers, sid, currentRevision),
      status: "complete",
      completedSteps: [...onboardingStepOrder],
      currentStep: "review",
    };

    const targetLabels = answers.goal?.targets.map((t) => t.label) ?? [];

    return {
      draft: completedDraft,
      answers,
      profileSeed: {
        resumeSignals: answers.resume?.parsedSignals ?? [],
        goalHeadline: answers.goal?.headline ?? "",
        targetLabels,
        selectedSourceKinds: answers.networkSources
          .filter((s) => s.intent !== "skip")
          .map((s) => s.kind),
        connectedSourceKinds: answers.networkSources
          .filter((s) => s.connected)
          .map((s) => s.kind),
        outreachComfort: answers.relationshipComfort.outreachComfort,
      },
      dashboardSeed: {
        mapHeadline: "My Action Plan",
        primaryGoal: answers.goal?.headline ?? "",
        nextActions: [
          "Review your warmest paths",
          "Pick one person to ask for context",
          "Draft a low-pressure intro",
        ],
        targetCount: targetLabels.length,
        networkInputCount: answers.manualContacts.length,
        resumeSignalCount: answers.resume?.parsedSignals.length ?? 0,
      },
      completedAt: new Date().toISOString(),
    };
  },

  // ─── AI proxies ──────────────────────────────────────────────────

  async parseCv(input: ParseCvInput): Promise<ParseCvResult> {
    return httpClient.post<ParseCvResult>("/api/onboarding-v2/parse-cv", { text: input.text });
  },

  async parseCvFile(input: ParseCvFileInput): Promise<ParseCvResult> {
    const fileName = input.file.name.toLowerCase();
    const isPdf = input.file.type === "application/pdf" || fileName.endsWith(".pdf");
    const isText = input.file.type.startsWith("text/") || fileName.endsWith(".txt");

    if (isPdf) return parsePdfCv(input.file);
    if (isText) return parseTextCv(input.file);

    throw new Error("Live resume review currently supports PDF files. Export this resume as a PDF and upload it again.");
  },

  async extractLinkedIn(input: ExtractLinkedInInput): Promise<LinkedInExtractionResult> {
    const formData = new FormData();
    // Field names must match the backend contract exactly. The browser sets the
    // multipart Content-Type (with boundary) automatically - do not set it here.
    // Repeat the same field name for each image (multer FilesInterceptor).
    for (const screenshot of input.screenshots.slice(0, 2)) {
      formData.append("screenshot", screenshot);
    }
    formData.append("profileUrl", input.profileUrl);
    return httpClient.postForm<LinkedInExtractionResult>("/api/onboarding/linkedin/extract", formData);
  },

  async suggestValues(input: SuggestValuesInput): Promise<SuggestValuesResult> {
    return httpClient.post<SuggestValuesResult>("/api/onboarding-v2/suggest-values", {
      keep: input.keep,
      context: input.context,
    });
  },

  async suggestFocusAreas(input: SuggestFocusAreasInput): Promise<SuggestFocusAreasResult> {
    // Wire returns snake_case `focus_areas`; normalize to camelCase here so callers see one shape.
    const res = await httpClient.post<{ focus_areas?: string[]; focusAreas?: string[] }>(
      "/api/onboarding-v2/suggest-focus-areas",
      { keep: input.keep, context: input.context, lookingFor: input.lookingFor },
    );
    return { focusAreas: res.focusAreas ?? res.focus_areas ?? [] };
  },

  async suggestRolesIndustries(
    input: SuggestRolesIndustriesInput,
  ): Promise<SuggestRolesIndustriesResult> {
    const res = await httpClient.post<{ roles?: string[]; industries?: string[] }>(
      "/api/onboarding-v2/suggest-roles-industries",
      {
        focusAreas: input.focusAreas,
        lookingFor: input.lookingFor,
        context: input.context,
        keepRoles: input.keepRoles,
        keepIndustries: input.keepIndustries,
      },
    );
    return { roles: res.roles ?? [], industries: res.industries ?? [] };
  },

  async generateQuiz(input: GenerateQuizInput): Promise<GenerateQuizResult> {
    return httpClient.post<GenerateQuizResult>(
      "/api/onboarding-v2/generate-quiz",
      input as unknown as Record<string, unknown>,
    );
  },

  async saveChatMemory(input: SaveChatMemoryInput): Promise<SaveChatMemoryResult> {
    // Mid-flow durability: patch profile Intro + reflection from chat answers.
    // (Legacy /chat-memory route is not implemented on the API.)
    const answers = input.answers;
    if (!answers) return { saved: true };
    const { experienceSummary, writtenReflection, linkedinUrl } =
      buildProfileReflectionFields(answers);
    if (!experienceSummary && !writtenReflection && !linkedinUrl) {
      return { saved: true };
    }
    try {
      await httpClient.patch("/api/onboarding/profile", {
        ...(experienceSummary ? { experience_summary: experienceSummary } : {}),
        ...(writtenReflection ? { written_reflection: writtenReflection } : {}),
        ...(linkedinUrl ? { linkedin_url: linkedinUrl } : {}),
      });
      return { saved: true };
    } catch {
      return { saved: false };
    }
  },

  async personalizedQuestion(input: PersonalizedQuestionInput): Promise<PersonalizedQuestionResult> {
    // Map the internal `step` naming to the backend's `fieldKey` at the wire
    // boundary, and always include `existingAnswers` as an object. The internal
    // PersonalizedQuestionInput keeps `step` so callers are unaffected.
    const body: Record<string, unknown> = {
      fieldKey: input.step,
      profileContext: input.profileContext ?? {},
      existingAnswers: input.existingAnswers ?? {},
    };
    if (input.alreadyResolved?.length) body.alreadyResolved = input.alreadyResolved;
    // Prior-answer context drives the acknowledgement; only sent when present
    // (the first step has no predecessor).
    if (input.previousFieldKey) body.previousFieldKey = input.previousFieldKey;
    if (typeof input.previousAnswer === "string") body.previousAnswer = input.previousAnswer;

    // Tolerate snake_case and loose option shapes from the wire so a backend
    // copy change can't break the (best-effort) caller; anything unexpected
    // normalizes to a fallback the caller already handles.
    const res = await httpClient.post<{
      source?: string;
      question?: string;
      acknowledgement?: string | null;
      placeholder?: string | null;
      options?: unknown;
      suggestedOptions?: unknown;
    }>("/api/onboarding/personalized-question", body);

    const options = normalizePersonalizedOptions(res.suggestedOptions ?? res.options);
    return {
      // The backend signals success with several source values ("ai",
      // "generated", …) and reserves "fallback" for its own static copy. Treat
      // only an explicit "fallback" (or a missing source) as fallback so AI copy
      // isn't discarded; the caller still guards against an empty question.
      source: res.source && res.source !== "fallback" ? "generated" : "fallback",
      question: typeof res.question === "string" ? res.question : "",
      acknowledgement: typeof res.acknowledgement === "string" ? res.acknowledgement : undefined,
      placeholder: typeof res.placeholder === "string" ? res.placeholder : undefined,
      options: options.length > 0 ? options : undefined,
    };
  },
};

// Accepts either ["Remote", ...] or [{ label, value }, ...] and returns a
// uniform {label, value}[]; drops anything that isn't a usable label.
function normalizePersonalizedOptions(raw: unknown): PersonalizedQuestionOption[] {
  if (!Array.isArray(raw)) return [];
  const options: PersonalizedQuestionOption[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      const label = item.trim();
      if (label) options.push({ label, value: label });
      continue;
    }
    if (item && typeof item === "object") {
      const record = item as { label?: unknown; value?: unknown };
      const label = typeof record.label === "string" ? record.label.trim() : "";
      if (!label) continue;
      const value = typeof record.value === "string" && record.value.trim() ? record.value.trim() : label;
      options.push({ label, value });
    }
  }
  return options;
}
